import React, { useState } from 'react';
import {
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useDispatch } from 'react-redux';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { showToast } from '@/features/toast/toastSlice';
import { useDeleteRelationshipContactMutation } from '@/features/relationshipContacts/relationshipContactsApi';
import { useObjectContext } from '@/hooks/useObjectContext';

const RelationshipCompanyActionsMenu = ({ contact }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { relationshipCompany } = useObjectContext();
	const [anchorEl, setAnchorEl] = useState(null);
	const [showDialog, setShowDialog] = useState(false);
	const [deleteRelationshipContact] = useDeleteRelationshipContactMutation();

	const isPrimary = Boolean(contact?.isPrimary);
	const contactName =
		contact?.fullName ||
		[contact?.firstName, contact?.lastName].filter(Boolean).join(' ');

	const handleClose = () => setAnchorEl(null);

	const handleConfirm = async () => {
		try {
			await deleteRelationshipContact({
				relationshipContactId: contact.id,
				relationshipCompanyId: relationshipCompany?.id,
			}).unwrap();
			dispatch(
				showToast({
					message: `${contactName} unlinked`,
					autohide: true,
					duration: 6000,
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					message:
						error?.data?.message ??
						error?.message ??
						`Error unlinking ${contactName}`,
					error: true,
				}),
			);
		} finally {
			setShowDialog(false);
		}
	};

	return (
		<>
			<IconButton
				aria-label='actions'
				size='small'
				onClick={(event) => {
					event.stopPropagation();
					setAnchorEl(event.currentTarget);
				}}
			>
				<MoreVertIcon />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				onClick={(event) => event.stopPropagation()}
			>
				<Tooltip
					title={
						isPrimary ? 'Set another primary contact before unlinking.' : ''
					}
				>
					<span>
						<MenuItem
							disabled={isPrimary || !relationshipCompany?.id}
							onClick={() => {
								handleClose();
								setShowDialog(true);
							}}
						>
							<ListItemIcon>
								<LinkOffIcon
									sx={{
										backgroundColor: theme.palette.primary.main,
										width: '22px',
										height: '22px',
										borderRadius: 2,
										color: 'white',
									}}
								/>
							</ListItemIcon>
							<ListItemText sx={{ color: theme.palette.primary.main }}>
								Unlink contact
							</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>
			</Menu>
			{showDialog && (
				<ConfirmDialog
					openDialog={showDialog}
					handleClose={() => setShowDialog(false)}
					handleConfirm={handleConfirm}
					actionName='unlink contact'
					canBeUndone={false}
					additionalContent={`${contactName} will be unlinked from ${relationshipCompany?.name}.`}
					onClick={(event) => event.stopPropagation()}
					onMouseDown={(event) => event.stopPropagation()}
				/>
			)}
		</>
	);
};

export default RelationshipCompanyActionsMenu;
